import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePhotoUpload from '../common/ProfilePhotoUpload';
import AdminAlertBell from './AdminAlertBell';
import useSupportUnread from '../../hooks/useSupportUnread';
import axios from 'axios';
import { 
  LayoutDashboard, Users, Calendar, Settings, LogOut, FileText, 
  Tag, AlertTriangle, BarChart3, UserCog, Grid3X3, Shield, Wallet, 
  PieChart, Database, Flag, CreditCard, MapPin, Image, ShieldCheck, ChevronDown, Clock,
  Calculator, Package, Bug, MessageSquare, Megaphone, FileSpreadsheet, Sparkles
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Grouped menu configuration
const menuGroups = [
  {
    label: 'Overview',
    items: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
      { path: '/admin/reports', icon: BarChart3, label: 'Reports', module: 'reports' },
      { path: '/admin/category-analytics', icon: PieChart, label: 'Category Analytics', module: 'reports' },
    ]
  },
  {
    label: 'Users & Providers',
    items: [
      { path: '/admin/users', icon: UserCog, label: 'Users', module: 'customers', adminOnly: true },
      { path: '/admin/providers', icon: Users, label: 'Providers', module: 'providers' },
      { path: '/admin/messages', icon: MessageSquare, label: 'Messages', module: 'support_chat' },
      { path: '/admin/knowledge-base', icon: Sparkles, label: 'Knowledge Base', module: 'support_chat' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/admin/bookings', icon: Calendar, label: 'Bookings', module: 'bookings' },
      { path: '/admin/categories', icon: Grid3X3, label: 'Categories', module: 'categories' },
      { path: '/admin/services', icon: Package, label: 'Services', module: 'services' },
      { path: '/admin/insurance-claims', icon: ShieldCheck, label: 'Insurance Claims', module: 'bookings' },
      { path: '/admin/insurance-declaration', icon: FileSpreadsheet, label: 'Insurance Declaration', adminOnly: true },
    ]
  },
  {
    label: 'Finance',
    items: [
      { path: '/admin/transactions', icon: CreditCard, label: 'Transactions', adminOnly: true },
      { path: '/admin/withdrawals', icon: Wallet, label: 'Withdrawals', module: 'withdrawals' },
      { path: '/admin/accounting', icon: Calculator, label: 'Accounting', module: 'accounting' },
    ]
  },
  {
    label: 'Moderation',
    items: [
      { path: '/admin/disputes', icon: AlertTriangle, label: 'Disputes', module: 'disputes' },
      { path: '/admin/user-reports', icon: Flag, label: 'User Reports', module: 'disputes', adminOnly: true },
    ]
  },
  {
    label: 'Marketing',
    items: [
      { path: '/admin/banners', icon: Image, label: 'Banners', module: 'cms' },
      { path: '/admin/promo-codes', icon: Tag, label: 'Promo Codes', module: 'promo_codes' },
      { path: '/admin/cms', icon: FileText, label: 'CMS', module: 'cms' },
      { path: '/admin/outreach', icon: Megaphone, label: 'Outreach', adminOnly: true },
    ]
  },
  {
    label: 'System',
    items: [
      { path: '/admin/locations', icon: MapPin, label: 'Locations', adminOnly: true },
      { path: '/admin/audit-logs', icon: Shield, label: 'Audit Logs', adminOnly: true },
      { path: '/admin/error-logs', icon: Bug, label: 'Error Logs', adminOnly: true },
      { path: '/admin/data-management', icon: Database, label: 'Data Management', adminOnly: true },
      { path: '/admin/data-retention', icon: Clock, label: 'Data Retention', adminOnly: true },
      { path: '/admin/settings', icon: Settings, label: 'Settings', adminOnly: true },
    ]
  },
];

export default function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useAuth();
  const { unread: supportUnread } = useSupportUnread();
  const [profileImage, setProfileImage] = useState(null);
  const [siteLogo, setSiteLogo] = useState(null);
  const [siteTitle, setSiteTitle] = useState('HandyBayani');
  const [siteUrl, setSiteUrl] = useState('https://handybayani.com');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  const fetchProfileImage = () => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setProfileImage(res.data?.profile_image || null))
        .catch(() => {});
    }
  };
  
  useEffect(() => {
    // Fetch profile image
    fetchProfileImage();
    
    // Fetch site identity (logo)
    axios.get(`${API}/cms/landing`)
      .then(res => {
        const siteIdentity = res.data?.site_identity;
        if (siteIdentity?.site_logo) {
          setSiteLogo(siteIdentity.site_logo.startsWith('http') ? siteIdentity.site_logo : `${BACKEND_URL}${siteIdentity.site_logo}`);
        } else {
          setSiteLogo('/handybayani-logo.png');
        }
        if (siteIdentity?.site_title) {
          setSiteTitle(siteIdentity.site_title);
        }
        if (siteIdentity?.site_url) {
          setSiteUrl(siteIdentity.site_url);
        }
      })
      .catch(() => {
        setSiteLogo('/handybayani-logo.png');
      });
      
    // Refresh profile image when window gains focus
    const handleFocus = () => fetchProfileImage();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);
  
  const handleImageUpdate = (url) => {
    setProfileImage(url);
    refreshUser?.();
  };
  
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const toggleGroup = (groupLabel) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel]
    }));
  };
  
  // Filter menu items based on user role and permissions
  const getVisibleGroups = () => {
    return menuGroups.map(group => {
      const visibleItems = group.items.filter(item => {
        // Admins can access everything
        if (user?.role === 'admin') return true;
        
        // For other staff roles (manager, customer_service, accountant)
        if (['manager', 'customer_service', 'accountant'].includes(user?.role)) {
          // Admin-only items are restricted
          if (item.adminOnly) return false;
          
          const permissions = user?.permissions || {};
          
          // Check if user has permission for this module
          if (item.module && permissions[item.module]?.can_view) return true;
          
          // Dashboard is accessible if user has ANY permission
          if (item.path === '/admin' && Object.values(permissions).some(p => p.can_view)) return true;
        }
        return false;
      });
      return { ...group, items: visibleItems };
    }).filter(group => group.items.length > 0);
  };
  
  const visibleGroups = getVisibleGroups();
  
  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <aside className="w-64 bg-slate-900 min-h-screen hidden md:flex md:flex-col fixed left-0 top-0 bottom-0 z-40">
      {/* Header with Logo */}
      <div className="p-4">
        <button 
          onClick={() => navigate('/admin')}
          className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity"
        >
          {siteLogo ? (
            <img 
              src={siteLogo} 
              alt={siteTitle}
              className="w-10 h-10 rounded-xl object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className={`w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center ${siteLogo ? 'hidden' : ''}`}
            style={{ display: siteLogo ? 'none' : 'flex' }}
          >
            <img src="/handybayani-logo.png" alt="HandyBayani" className="w-8 h-8 object-contain" />
          </div>
          <span className="text-white font-bold text-lg font-[Poppins]">{siteTitle}</span>
        </button>
        
        {/* Alert bell */}
        <AdminAlertBell />
        
        {/* Role badge */}
        {user?.role === 'manager' && (
          <div className="px-4 py-2 bg-indigo-500/20 rounded-xl border border-indigo-500/30">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-indigo-400" />
              <span className="text-sm text-indigo-300 font-medium">Manager</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Scrollable Navigation with Groups */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {visibleGroups.map((group, groupIndex) => (
          <div key={group.label} className={groupIndex > 0 ? 'mt-4' : ''}>
            {/* Group Header */}
            <button
              onClick={() => toggleGroup(group.label)}
              className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-400"
            >
              <span>{group.label}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${collapsedGroups[group.label] ? '-rotate-90' : ''}`} />
            </button>
            
            {/* Group Items */}
            {!collapsedGroups[group.label] && (
              <div className="mt-1 space-y-0.5">
                {group.items.map((item) => (
                  <button
                    key={item.path}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                      isActive(item.path)
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                    }`}
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.path === '/admin/messages' && supportUnread > 0 && (
                      <span
                        className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold"
                        data-testid="sidebar-support-unread-badge"
                      >
                        {supportUnread > 99 ? '99+' : supportUnread}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
      
      {/* User info and logout - Fixed at bottom */}
      <div className="p-4 border-t border-slate-800 bg-slate-900">
        <div className="flex items-center gap-3 mb-3">
          <ProfilePhotoUpload
            currentImage={profileImage}
            userName={user?.name}
            onImageUpdate={handleImageUpdate}
            size="sm"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-300 font-medium truncate">{user?.name || 'Admin'}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
        </div>
        <button 
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Logout
        </button>
      </div>
    </aside>
  );
}
